import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { EpicProgress, LeftOut, RelatedTicket, SubTicketPlan, Ticket, TicketConsistency } from '../api/types.js';
import { parseIssueRef } from '../api/pr-ref.js';
import { useCheckConsistency, useTicketConsistency } from '../hooks/use-tickets.js';
import { timeAgo } from '../utils/time-ago.js';
import { Button } from './button.js';
import { Inline, PlainText, TextBody } from './plain-text.js';

// Epics (ADR-026 section 5) and what ties the plans together: an epic's breakdown, edited before Create the
// sub-tickets, then where its sub-issues stand; the relations a plan declares to the other plans; the consistency check
// across all of them. Every text was written by a session that read issues from others: plain text only.

const field = 'block w-full rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus-visible:outline focus-visible:outline-2';
const fieldStyle = { backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border-2)', color: 'var(--color-text)' };
const linkStyle = { color: 'var(--color-brand-light)' };
const MAX_SUB_TITLE = 200;
const MAX_SUB_GOAL = 3000;

/** Where a ticket's card is on this page */
const cardHref = (serverId: string, ref: string) => `/servers/${serverId}/tickets/${encodeURIComponent(ref)}`;
const shortRef = (ref: string) => ref.replace(/^github:/, '');

/** A titled part of a plan */
export function Part({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <h4 className="mb-1.5 text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>{title}</h4>
      {children}
    </div>
  );
}

/** Open questions: each one structured like the rest of the plan */
export function Questions({ questions }: { questions: string[] }) {
  if (!questions.length) return null;
  return (
    <Part title={questions.length === 1 ? 'Open question' : `Open questions (${questions.length})`}>
      <ul className="list-disc space-y-2 pl-5 text-sm marker:text-(--color-text-3)" style={{ color: 'var(--color-text)' }}>
        {questions.map((q, i) => <li key={i} className="pl-1"><TextBody text={q} /></li>)}
      </ul>
    </Part>
  );
}

// ---------------------------------------------------------------------------
// Related plans
// ---------------------------------------------------------------------------

const RELATION: Record<RelatedTicket['relation'], string> = {
  duplicate: 'Same work as',
  overlaps: 'Partly the same work as',
  'depends-on': 'Needs first',
  blocks: 'Has to come before',
};

/** How this plan relates to the server's other plans, each linked to its card */
export function RelatedPlans({ serverId, related, titles, pending }: {
  serverId: string; related: RelatedTicket[]; titles: Map<string, string | undefined>; pending: boolean;
}) {
  if (!related.length) return null;
  const duplicate = related.find((r) => r.relation === 'duplicate');
  return (
    <div className="min-w-0">
      <h4 className="mb-1 text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>Related plans</h4>
      <ul className="space-y-1 text-sm" style={{ color: 'var(--color-text)' }}>
        {related.map((r) => (
          <li key={`${r.ref} ${r.relation}`} className="break-words">
            {RELATION[r.relation]}{' '}
            <Link to={cardHref(serverId, r.ref)} className="hover:underline focus-visible:outline focus-visible:outline-2" style={linkStyle}>
              {shortRef(r.ref)}{titles.get(r.ref) ? ` ${titles.get(r.ref)}` : ''}
            </Link>
            {r.note && <span style={{ color: 'var(--color-text-2)' }}>: {r.note}</span>}
          </li>
        ))}
      </ul>
      {duplicate && pending && (
        <p className="mt-1.5 text-xs" style={{ color: 'var(--color-warn)' }}>
          The session thinks {shortRef(duplicate.ref)} is the same work. If it is, reject this one and keep the other.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The breakdown, as proposed and as edited
// ---------------------------------------------------------------------------

const SIZE: Record<SubTicketPlan['size'], string> = { S: 'small', M: 'medium', L: 'large' };

/** A sub-ticket the checks left out, with what the breakdown editor needs to put it back */
type LeftOutSub = LeftOut & { index: number; subTicket: NonNullable<LeftOut['subTicket']> };

const leftOutSubs = (t: Ticket): LeftOutSub[] => (t.leftOut ?? [])
  .filter((l): l is LeftOutSub => l.field === 'subTickets' && !!l.subTicket && l.index !== undefined);

type Entry = { kind: 'kept'; s: SubTicketPlan; n: number } | { kind: 'left'; l: LeftOutSub };

/** The breakdown as the session proposed it: the kept sub-tickets in order, each left-out one where its order put it */
function breakdownEntries(t: Ticket): Entry[] {
  const kept = t.epic?.subTickets ?? [];
  const left = leftOutSubs(t);
  const out: Entry[] = [];
  kept.forEach((s, n) => {
    for (const l of left) if (l.subTicket.at === n) out.push({ kind: 'left', l });
    out.push({ kind: 'kept', s, n });
  });
  for (const l of left) if (l.subTicket.at >= kept.length) out.push({ kind: 'left', l });
  return out;
}

const quoted = (titles: string[]) => titles.map((x) => `« ${x} »`).join(', ');

/** Text shown in a dashed, muted frame: not part of the plan, kept so the operator can see it */
const leftOutFrame = 'border-l-2 border-dashed pl-2.5';
const leftOutStyle = { borderColor: 'var(--color-border-2)', color: 'var(--color-text-3)' };

interface Row {
  title: string;
  goal: string;
  size: SubTicketPlan['size'];
  /** Positions in the rows */
  dependsOn: number[];
  removed: boolean;
  /** Why the checks left it out: out of the breakdown until the operator puts it back */
  leftOut?: string;
}

/** The rows of the editor: the kept sub-tickets, and the left-out ones in their place, out until put back */
function initialRows(t: Ticket): Row[] {
  const entries = breakdownEntries(t);
  const ofKept = new Map<number, number>();
  const ofLeft = new Map<number, number>();
  entries.forEach((e, i) => (e.kind === 'kept' ? ofKept.set(e.n, i) : ofLeft.set(e.l.index, i)));
  const deps = (kept: number[], left: number[] = []) => [
    ...kept.flatMap((d) => (ofKept.has(d) ? [ofKept.get(d)!] : [])), ...left.flatMap((d) => (ofLeft.has(d) ? [ofLeft.get(d)!] : [])),
  ].sort((a, b) => a - b);
  return entries.map((e) => (e.kind === 'kept'
    ? { title: e.s.title, goal: e.s.goal, size: e.s.size, dependsOn: deps(e.s.dependsOn, e.s.dependsOnLeftOut), removed: false }
    : { title: e.l.subTicket.title, goal: e.l.content, size: e.l.subTicket.size, dependsOn: deps(e.l.subTicket.dependsOn, e.l.subTicket.dependsOnLeftOut), removed: true, leftOut: e.l.reason }));
}

/** The edited breakdown as the API takes it: the kept sub-tickets in order, dependencies renumbered among them */
function keptSubTickets(rows: Row[]): Array<{ title: string; goal: string; dependsOn: number[] }> {
  const position = new Map<number, number>();
  rows.forEach((r, i) => { if (!r.removed) position.set(i, position.size); });
  return rows.flatMap((r) => (r.removed ? [] : [{
    title: r.title.trim(), goal: r.goal.trim(),
    dependsOn: r.dependsOn.filter((d) => position.has(d)).map((d) => position.get(d)!),
  }]));
}

// Backticks that make a mention inert do not count (as for the API)
const visible = (s: string) => s.replace(/`/g, '').length;
const rowProblem = (r: Row) => !r.title.trim() ? 'needs a title' : visible(r.title) > MAX_SUB_TITLE ? `has a title over ${MAX_SUB_TITLE} characters`
  : !r.goal.trim() ? 'needs a goal' : visible(r.goal) > MAX_SUB_GOAL ? `has a goal over ${MAX_SUB_GOAL} characters` : null;

/** Create the sub-tickets: the breakdown editable (title, goal, which to keep), in its order; a left-out one can be put back */
export function BreakdownForm({ t, pending, onSubmit, onCancel }: {
  t: Ticket; pending: boolean; onSubmit: (subTickets: Array<{ title: string; goal: string; dependsOn: number[] }>) => void; onCancel: () => void;
}) {
  const [rows, setRows] = useState<Row[]>(() => initialRows(t));
  const issue = parseIssueRef(t.externalRef);
  const set = (i: number, patch: Partial<Row>) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const shown = new Map<number, number>();
  rows.forEach((r, i) => { if (!r.removed) shown.set(i, shown.size + 1); });
  const kept = rows.filter((r) => !r.removed);
  const problem = kept.map((r, i) => [i + 1, rowProblem(r)] as const).find(([, p]) => p);
  const done = new Set((t.subTickets ?? []).filter((r) => r.number && r.attached).map((r) => r.title.trim().toLowerCase()));
  const name = (r: Row) => r.title.trim() || 'Untitled sub-ticket';

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!kept.length || problem) return;
    onSubmit(keptSubTickets(rows));
  }

  return (
    <form onSubmit={submit} aria-label="Create the sub-tickets" className="space-y-3 rounded-md border p-3"
      style={{ borderColor: 'var(--color-border-2)', backgroundColor: 'var(--color-surface-2)' }}>
      <p className="text-xs" style={{ color: 'var(--color-text-2)' }}>
        aidev opens each sub-ticket you keep as an issue of {issue?.org}/{issue?.repo} under your name, unassigned, and attaches it to #{issue?.number}.
        The ones it waits for come first, and its description names them. Plain text: an @name is put between backticks so it notifies no one;
        no images, no links outside {issue?.org}.
      </p>
      <ol className="space-y-2">
        {rows.map((r, i) => {
          const n = shown.get(i);
          const label = `sub-ticket ${n ?? i + 1}`;
          if (r.removed && r.leftOut) {
            return (
              <li key={i} className={`${leftOutFrame} flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-1 text-xs`} style={leftOutStyle}>
                <span className="min-w-0 break-words">
                  <span className="font-medium"><Inline text={name(r)} /></span>
                  <span className="block">Left out by the checks: it {r.leftOut}.</span>
                </span>
                <Button type="button" size="sm" variant="ghost" className="max-sm:min-h-10" onClick={() => set(i, { removed: false })}
                  aria-label={`Put back ${name(r)}`}>Put back</Button>
              </li>
            );
          }
          if (r.removed) {
            return (
              <li key={i} className="flex flex-wrap items-center gap-2 text-xs" style={{ color: 'var(--color-text-3)' }}>
                <span className="min-w-0 break-words line-through">{name(r)}</span>
                <Button type="button" size="sm" variant="ghost" className="max-sm:min-h-10" onClick={() => set(i, { removed: false })}
                  aria-label={`Keep ${r.title || 'this sub-ticket'}`}>Keep</Button>
              </li>
            );
          }
          const after = r.dependsOn.filter((d) => shown.has(d)).map((d) => shown.get(d)!);
          const waiting = r.dependsOn.filter((d) => !shown.has(d) && rows[d]?.leftOut).map((d) => name(rows[d]));
          const lost = r.dependsOn.some((d) => !shown.has(d) && !rows[d]?.leftOut);
          const noteId = `breakdown-${i}-left-out`;
          return (
            <li key={i} className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-x-2 rounded-md p-2" style={{ backgroundColor: 'var(--color-surface)' }}>
              <span aria-hidden className="pt-1.5 text-right text-sm font-semibold tabular-nums" style={{ color: 'var(--color-text-3)' }}>{n}</span>
              <div className="min-w-0 space-y-1.5">
                {r.leftOut && (
                  <p id={noteId} className="text-xs" style={{ color: 'var(--color-warn)' }}>
                    Put back: the checks left it out because it {r.leftOut}. Fix it here; aidev checks it again when you create the sub-tickets.
                  </p>
                )}
                <label className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
                  <span className="sr-only">Title of {label}</span>
                  <input value={r.title} onChange={(e) => set(i, { title: e.target.value.replace(/[\r\n]+/g, ' ') })}
                    className={`${field} font-medium`} style={fieldStyle} aria-label={`Title of ${label}`} aria-describedby={r.leftOut ? noteId : undefined} />
                </label>
                <label className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
                  <span className="sr-only">Goal of {label}</span>
                  <textarea value={r.goal} onChange={(e) => set(i, { goal: e.target.value })} rows={3}
                    className={`${field} font-[inherit]`} style={fieldStyle} aria-label={`Goal of ${label}`} />
                </label>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs" style={{ color: 'var(--color-text-3)' }}>
                  <span>
                    {SIZE[r.size]}
                    {after.length > 0 && `, after ${after.join(', ')}`}
                    {waiting.length > 0 && `, waited for ${quoted(waiting)}, left out: put it back to wait for it again`}
                    {lost && ', no longer waits for a removed one'}
                    {done.has(r.title.trim().toLowerCase()) && ', already created'}
                  </span>
                  <Button type="button" size="sm" variant="ghost" className="max-sm:min-h-10" onClick={() => set(i, { removed: true })}
                    aria-label={`Remove ${label}${r.title ? `: ${r.title}` : ''}`}>Remove</Button>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
      {problem && <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>Sub-ticket {problem[0]} {problem[1]}.</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" className="max-sm:min-h-10" disabled={pending || !kept.length || !!problem}>
          {pending ? 'Creating…' : kept.length === 1 ? 'Create 1 sub-ticket' : `Create ${kept.length} sub-tickets`}
        </Button>
        <Button type="button" size="sm" variant="ghost" className="max-sm:min-h-10" onClick={onCancel} disabled={pending}>Cancel</Button>
      </div>
    </form>
  );
}

const FIELD_LABEL: Record<string, string> = { replyDraft: 'Draft reply', handoverDraft: 'Handover draft', related: 'Relation to another plan' };

/** The mentions made inert in what was kept: said once, with the tokens */
export function InertMentions({ t }: { t: Ticket }) {
  const tokens = [...new Set((t.neutralised ?? []).map((m) => m.token))];
  if (!tokens.length) return null;
  return (
    <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>
      {tokens.length === 1 ? '1 mention made inert' : `${tokens.length} mentions made inert`} (put between backticks, so GitHub notifies no one):{' '}
      {tokens.map((tok, i) => <span key={tok}>{i > 0 && ', '}<Inline text={`\`${tok}\``} /></span>)}.
    </p>
  );
}

/**
 * What the checks left out besides sub-tickets (drafts, relations): each with its reason and its content, muted, so
 * the operator sees what is missing. A plan collected before the content was kept says only why.
 */
export function LeftOutNotes({ t }: { t: Ticket }) {
  if (!t.leftOut) {
    const rejected = t.rejected.filter((r) => t.kind !== 'epic' || !r.field.startsWith('subTickets'));
    const subs = t.kind === 'epic' ? t.rejected.filter((r) => r.field.startsWith('subTickets')) : [];
    if (!rejected.length && !subs.length) return null;
    return (
      <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>
        Left out by the checks: {[...subs, ...rejected].map((r) => `${r.field.replace(/^subTickets\[(\d+)\]$/, (_m, n: string) => `sub-ticket ${Number(n) + 1}`)
          .replace(/^related\[\d+\]$/, 'a relation').replace(/^replyDraft$/, 'the draft reply').replace(/^handoverDraft$/, 'the handover draft')} (${r.reason})`).join('; ')}.
      </p>
    );
  }
  const others = t.leftOut.filter((l) => l.field !== 'subTickets');
  if (!others.length) return null;
  return (
    <Part title="Left out by the checks">
      <ul className="space-y-2.5" aria-label="Left out by the checks">
        {others.map((l, i) => (
          <li key={i} className={leftOutFrame} style={leftOutStyle}>
            <p className="text-xs">
              <span className="font-medium">{FIELD_LABEL[l.field] ?? l.field}</span>, not kept: it {l.reason}.
            </p>
            {l.content && <div className="mt-0.5"><PlainText text={l.content} className="text-xs" color="var(--color-text-3)" /></div>}
          </li>
        ))}
      </ul>
    </Part>
  );
}

/** The epic's plan: what it asks, what exists, the proposed breakdown (read-only), what the checks left out of it */
export function EpicPlanView({ t }: { t: Ticket }) {
  const p = t.epic;
  if (!p) {
    return (
      <p className="rounded-md px-3 py-2 text-sm" style={{ backgroundColor: 'var(--color-error-dim)', color: 'var(--color-error)' }}>
        The session&apos;s breakdown was refused by the checks: {t.errors.join('; ')}. A new event on the epic brings a new one; reject this one meanwhile.
      </p>
    );
  }
  const entries = breakdownEntries(t);
  const left = leftOutSubs(t);
  const leftTitle = new Map(left.map((l) => [l.index, l.subTicket.title]));
  const n = p.subTickets.length;
  const heading = `Proposed breakdown, ${n === 1 ? '1 sub-ticket' : `${n} sub-tickets`}${left.length ? `, ${left.length} left out` : ''}`;
  return (
    <div className="space-y-5">
      <Part title="Summary"><PlainText text={p.summary} /></Part>
      {p.existing && <Part title="What exists already"><PlainText text={p.existing} /></Part>}
      {t.status === 'pending' && (
        <Part title={heading}>
          {entries.length === 0 ? <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>Nothing left to break down: the existing sub-issues cover the epic.</p> : (
            <ol className="space-y-3 text-sm">
              {entries.map((e) => {
                if (e.kind === 'left') {
                  const s = e.l.subTicket;
                  const waits = [...s.dependsOn.map((d) => String(d + 1)), ...s.dependsOnLeftOut.flatMap((d) => (leftTitle.has(d) ? [`« ${leftTitle.get(d)} »`] : []))];
                  return (
                    <li key={`left-${e.l.index}`} className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-x-2">
                      <span aria-hidden className="text-right" style={{ color: 'var(--color-text-3)' }}>–</span>
                      <div className={`min-w-0 ${leftOutFrame}`} style={leftOutStyle}>
                        <span className="sr-only">Left out: </span>
                        <span className="break-words font-medium"><Inline text={s.title} /></span>
                        <span className="text-xs"> {SIZE[s.size]}{waits.length ? `, after ${waits.join(', ')}` : ''}</span>
                        <p className="text-xs">Left out by the checks: it {e.l.reason}. Put it back in Create the sub-tickets to fix it.</p>
                        {e.l.content && <div className="mt-0.5"><PlainText text={e.l.content} className="text-xs" color="var(--color-text-3)" /></div>}
                      </div>
                    </li>
                  );
                }
                const { s, n: i } = e;
                const waited = (s.dependsOnLeftOut ?? []).flatMap((d) => (leftTitle.has(d) ? [leftTitle.get(d)!] : []));
                return (
                  <li key={i} className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-x-2">
                    <span aria-hidden className="text-right font-semibold tabular-nums" style={{ color: 'var(--color-text-3)' }}>{i + 1}</span>
                    <div className="min-w-0">
                      <span className="sr-only">{i + 1}. </span>
                      <span className="break-words font-medium" style={{ color: 'var(--color-text)' }}><Inline text={s.title} /></span>
                      <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>
                        {' '}{SIZE[s.size]}{s.dependsOn.length ? `, after ${s.dependsOn.map((d) => d + 1).join(', ')}` : ''}
                        {waited.length > 0 && `, waited for ${quoted(waited)}, left out`}
                      </span>
                      <div className="mt-0.5"><PlainText text={s.goal} className="text-xs" color="var(--color-text-2)" /></div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </Part>
      )}
      <Questions questions={p.questions} />
      <LeftOutNotes t={t} />
      <InertMentions t={t} />
    </div>
  );
}

/** What Create the sub-tickets did, one line per sub-ticket (after a partial failure, or once broken down) */
export function SubTicketResults({ t }: { t: Ticket }) {
  const results = t.subTickets ?? [];
  if (!results.length) return null;
  return (
    <ul className="space-y-1 text-sm" aria-label="Sub-tickets created">
      {results.map((r, i) => (
        <li key={i} className="break-words">
          {r.number ? (
            <a href={r.url || undefined} target="_blank" rel="noreferrer" className="hover:underline" style={linkStyle}>#{r.number}</a>
          ) : <span style={{ color: 'var(--color-text-3)' }}>not created</span>}{' '}
          <span style={{ color: 'var(--color-text)' }}><Inline text={r.title} /></span>
          {r.error && <span className="block text-xs" style={{ color: 'var(--color-error)' }}>{r.error}</span>}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

const GROUPS = [
  { key: 'done', label: 'Done', color: 'var(--color-success)', fills: true },
  { key: 'inProgress', label: 'In progress', color: 'var(--color-brand)', fills: true },
  { key: 'blocked', label: 'Blocked', color: 'var(--color-warn)', fills: false },
  { key: 'remaining', label: 'Not started', color: 'var(--color-border-2)', fills: false },
] as const;

/**
 * Where the epic's sub-issues stand: a bar filled by what moved (done, then in progress), then each group with its
 * links. Blocked and not started leave the bar empty: drawn in, a bar with nothing done looked two thirds full.
 */
export function EpicProgressView({ t }: { t: Ticket }) {
  const p: EpicProgress | undefined = t.progress;
  const issue = parseIssueRef(t.externalRef);
  if (!p || !issue) {
    return t.status === 'broken-down'
      ? <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>The session is reading the sub-issues: their progress shows here after its run.</p>
      : null;
  }
  const titles = new Map((t.subIssues ?? []).map((s) => [s.number, s.title]));
  const url = (n: number) => `https://github.com/${issue.org}/${issue.repo}/issues/${n}`;
  const counts = { done: p.done.length, inProgress: p.inProgress.length, blocked: p.blocked.length, remaining: p.remaining.length };
  const total = counts.done + counts.inProgress + counts.blocked + counts.remaining;
  const sub = (n: number) => (
    <>
      <a href={url(n)} target="_blank" rel="noreferrer" className="hover:underline" style={linkStyle}>#{n}</a>
      {titles.get(n) && <span style={{ color: 'var(--color-text)' }}> {titles.get(n)}</span>}
    </>
  );
  const items: Record<(typeof GROUPS)[number]['key'], React.ReactNode[]> = {
    done: p.done.map((n) => <li key={n}>{sub(n)}</li>),
    inProgress: p.inProgress.map((x) => (
      <li key={x.n}>{sub(x.n)}<span style={{ color: 'var(--color-text-3)' }}>
        {x.assignee ? `, ${x.assignee}` : ''}{x.pr ? <>, <a href={`https://github.com/${issue.org}/${issue.repo}/pull/${x.pr}`} target="_blank" rel="noreferrer" className="hover:underline" style={linkStyle}>PR #{x.pr}</a></> : null}
      </span></li>
    )),
    blocked: p.blocked.map((x) => <li key={x.n}>{sub(x.n)}<span style={{ color: 'var(--color-text-3)' }}>, waits for {x.by.map((b) => `#${b}`).join(', ')}</span></li>),
    remaining: p.remaining.map((n) => <li key={n}>{sub(n)}</li>),
  };
  const sentence = `${counts.done} of ${total} done${counts.inProgress ? `, ${counts.inProgress} in progress` : ''}`;
  return (
    <section className="min-w-0" aria-label="Progress of the epic">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <h4 className="text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>
          Progress{total ? `: ${counts.done} of ${total} sub-issues done` : ''}
        </h4>
        {t.progressAt && <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>updated {timeAgo(t.progressAt)}</span>}
      </div>
      {total > 0 && (
        <div role="img" aria-label={sentence} className="mt-1.5 flex h-2 w-full overflow-hidden rounded-full" style={{ backgroundColor: 'var(--color-surface-3)' }}>
          {GROUPS.map((g) => g.fills && counts[g.key] > 0 && <span key={g.key} style={{ width: `${(counts[g.key] / total) * 100}%`, backgroundColor: g.color }} />)}
        </div>
      )}
      {t.progressAlert && (
        <p className="mt-2 text-sm font-medium" style={{ color: t.progressAlert.kind === 'done' ? 'var(--color-success)' : 'var(--color-warn)' }}>{t.progressAlert.text}</p>
      )}
      {p.summary && <div className="mt-2"><PlainText text={p.summary} /></div>}
      <div className="mt-2 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        {GROUPS.filter((g) => counts[g.key]).map((g) => (
          <div key={g.key} className="min-w-0">
            <h5 className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>
              <span aria-hidden className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: g.color }} />
              {g.label} ({counts[g.key]})
            </h5>
            <ul className="mt-0.5 space-y-0.5 break-words text-sm">{items[g.key]}</ul>
          </div>
        ))}
      </div>
      {t.progressErrors && t.progressErrors.length > 0 && (
        <p className="mt-2 text-xs" style={{ color: 'var(--color-text-3)' }}>The last progress update was refused by the checks: {t.progressErrors.join('; ')}.</p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Consistency check
// ---------------------------------------------------------------------------

const FINDING: Record<NonNullable<TicketConsistency['report']>['findings'][number]['kind'], string> = {
  duplicate: 'The same work twice',
  conflict: 'Plans that contradict each other',
  order: 'An order that cannot work',
  gap: 'Something no plan covers',
  overlap: 'Plans that partly do the same work',
};

/** « Check consistency » and the latest report, findings linked to the cards */
export function ConsistencyCheck({ serverId, isAdmin, titles }: { serverId: string; isAdmin: boolean; titles: Map<string, string | undefined> }) {
  const { data: c } = useTicketConsistency(serverId);
  const check = useCheckConsistency(serverId);
  const running = c?.status === 'running' || check.isPending;
  const error = check.error as Error | null;
  const n = c?.plansChecked.length ?? 0;
  if (!c && !isAdmin) return null;
  return (
    <div id="tickets-consistency" className="mt-4 rounded-md border px-3 py-2.5" style={{ borderColor: 'var(--color-border)' }} aria-live="polite">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 text-sm" style={{ color: 'var(--color-text-2)' }}>
          {running ? `Checking ${n || 'the'} plans together…${c?.requestedAt ? ` Started ${timeAgo(c.requestedAt)}.` : ''}`
            : c?.status === 'failed' ? `The last check did not finish: ${(c.errors ?? []).join('; ') || 'no report'}.`
              : c?.status === 'done' ? `Checked ${n} plans together ${timeAgo(c.collectedAt ?? c.requestedAt)}.`
                : 'Check the open plans together for duplicates, conflicts, order problems and gaps.'}
        </p>
        {isAdmin && (
          <Button size="sm" variant="ghost" className="max-sm:min-h-10" disabled={running} onClick={() => check.mutate()}>
            {running ? 'Checking…' : 'Check consistency'}
          </Button>
        )}
      </div>
      {error && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>Not started: {error.message}</p>}
      {c?.status === 'done' && c.report && (
        c.report.findings.length === 0
          ? <p className="mt-1 text-sm" style={{ color: 'var(--color-success)' }}>No inconsistency found among {n} plans.</p>
          : (
            <>
              {c.report.summary && <div className="mt-1 max-w-[72ch]"><PlainText text={c.report.summary} /></div>}
              <ul className="mt-2 space-y-2">
                {c.report.findings.map((f, i) => (
                  <li key={i} className="border-l-2 pl-3 text-sm" style={{ borderColor: f.kind === 'conflict' || f.kind === 'order' ? 'var(--color-warn)' : 'var(--color-border-2)' }}>
                    <span className="font-medium" style={{ color: 'var(--color-text)' }}>{FINDING[f.kind]}: </span>
                    {f.refs.map((r, j) => (
                      <span key={r}>{j > 0 && ', '}
                        <Link to={cardHref(serverId, r)} className="hover:underline focus-visible:outline focus-visible:outline-2" style={linkStyle}>
                          {shortRef(r)}{titles.get(r) ? ` ${titles.get(r)}` : ''}
                        </Link>
                      </span>
                    ))}
                    <div className="mt-0.5 max-w-[72ch]"><TextBody text={f.note} /></div>
                    {f.suggestion && <div className="mt-1 max-w-[72ch]"><TextBody text={f.suggestion} className="text-xs" color="var(--color-text-2)" /></div>}
                  </li>
                ))}
              </ul>
            </>
          )
      )}
    </div>
  );
}
